// Math problem generation engine

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function getRange(difficulty) {
  switch (difficulty) {
    case 'easy': return { min: 1, max: 9 };
    case 'medium': return { min: 10, max: 99 };
    case 'hard': return { min: 100, max: 999 };
    default: return { min: 1, max: 9 };
  }
}

function generateFraction(difficulty) {
  const { min, max } = getRange(difficulty);
  const num = randInt(min, max);
  const den = randInt(Math.max(2, min), max);
  return { num, den };
}

function generateMixedFraction(small) {
  const whole = small ? randInt(1, 2) : randInt(1, 3);
  const den = small ? randInt(2, 4) : randInt(2, 6);
  const fracNum = randInt(1, den - 1);
  const g = gcd(fracNum, den);
  const simpleDen = den / g;
  const simpleNum = fracNum / g;
  return { num: whole * simpleDen + simpleNum, den: simpleDen };
}

function simplifyFraction(num, den) {
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function fractionsEqual(a, b) {
  return a.num * b.den === b.num * a.den;
}

function formatFraction(f) {
  return { num: f.num, den: f.den };
}

// Generate a natural number problem
function generateNaturalProblem(difficulty, operation) {
  const { min, max } = getRange(difficulty);
  let a, b, answer;

  switch (operation) {
    case 'add':
      a = randInt(min, max);
      b = randInt(min, max);
      answer = a + b;
      return { display: `${a} + ${b}`, answer, type: 'natural' };

    case 'subtract':
      a = randInt(min, max);
      b = randInt(min, max);
      if (b > a) [a, b] = [b, a]; // keep positive
      answer = a - b;
      return { display: `${a} − ${b}`, answer, type: 'natural' };

    case 'multiply':
      if (difficulty === 'hard') {
        a = randInt(10, 99);
        b = randInt(2, 9);
      } else {
        a = randInt(min, max);
        b = randInt(min, max);
      }
      answer = a * b;
      return { display: `${a} × ${b}`, answer, type: 'natural' };

    case 'divide':
      b = randInt(Math.max(2, min), difficulty === 'hard' ? 30 : max);
      answer = randInt(min, max);
      a = b * answer; // ensure clean division
      return { display: `${a} ÷ ${b}`, answer, type: 'natural' };

    default:
      return generateNaturalProblem(difficulty, 'add');
  }
}

// Generate fraction problems
function generateFractionProblem(difficulty, operation) {
  switch (operation) {
    case 'add':
    case 'subtract': {
      let f1, f2;
      if (difficulty === 'medium') {
        f1 = generateMixedFraction();
        f2 = generateMixedFraction();
        // Ensure positive result for subtraction
        if (operation === 'subtract' && f1.num * f2.den < f2.num * f1.den) {
          [f1, f2] = [f2, f1];
        }
      } else {
        f1 = generateFraction(difficulty === 'hard' ? 'medium' : 'easy');
        f2 = generateFraction(difficulty === 'hard' ? 'medium' : 'easy');
      }
      const commonDen = lcm(f1.den, f2.den);
      const num1 = f1.num * (commonDen / f1.den);
      const num2 = f2.num * (commonDen / f2.den);
      const resultNum = operation === 'add' ? num1 + num2 : num1 - num2;
      const simplified = simplifyFraction(Math.abs(resultNum), commonDen);
      const sign = resultNum < 0 ? -1 : 1;
      return {
        display: null,
        fraction1: f1,
        fraction2: f2,
        operator: operation === 'add' ? '+' : '−',
        answer: { num: simplified.num * sign, den: simplified.den },
        type: 'fraction',
        isMixed: difficulty === 'medium',
      };
    }

    case 'multiply': {
      let f1, f2;
      if (difficulty === 'medium') {
        f1 = generateMixedFraction(true);
        f2 = generateMixedFraction(true);
      } else {
        f1 = generateFraction(difficulty === 'hard' ? 'medium' : 'easy');
        f2 = generateFraction(difficulty === 'hard' ? 'medium' : 'easy');
      }
      const resultNum = f1.num * f2.num;
      const resultDen = f1.den * f2.den;
      const simplified = simplifyFraction(resultNum, resultDen);
      return {
        display: null,
        fraction1: f1,
        fraction2: f2,
        operator: '×',
        answer: simplified,
        type: 'fraction',
        isMixed: difficulty === 'medium',
      };
    }

    case 'divide': {
      let f1, f2;
      if (difficulty === 'medium') {
        f1 = generateMixedFraction(true);
        f2 = generateMixedFraction(true);
      } else {
        f1 = generateFraction(difficulty === 'hard' ? 'medium' : 'easy');
        f2 = generateFraction(difficulty === 'hard' ? 'medium' : 'easy');
      }
      const resultNum = f1.num * f2.den;
      const resultDen = f1.den * f2.num;
      const simplified = simplifyFraction(resultNum, resultDen);
      return {
        display: null,
        fraction1: f1,
        fraction2: f2,
        operator: '÷',
        answer: simplified,
        type: 'fraction',
        isMixed: difficulty === 'medium',
      };
    }

    case 'expand': {
      const base = generateFraction('easy');
      const simplified = simplifyFraction(base.num, base.den);
      const factor = randInt(2, difficulty === 'hard' ? 12 : difficulty === 'medium' ? 8 : 5);
      const expanded = { num: simplified.num * factor, den: simplified.den * factor };
      return {
        display: null,
        fraction1: expanded,
        instruction: 'Kürze den Bruch',
        answer: simplified,
        type: 'fraction-simplify'
      };
    }

    case 'reduce': {
      const base = generateFraction('easy');
      const simplified = simplifyFraction(base.num, base.den);
      const factor = randInt(2, difficulty === 'hard' ? 12 : difficulty === 'medium' ? 8 : 5);
      return {
        display: null,
        fraction1: simplified,
        targetDen: simplified.den * factor,
        instruction: `Erweitere auf Nenner ${simplified.den * factor}`,
        answer: { num: simplified.num * factor, den: simplified.den * factor },
        type: 'fraction-expand'
      };
    }

    default:
      return generateFractionProblem(difficulty, 'add');
  }
}

// Generate wrong answers that look plausible
function generateWrongAnswers(correctAnswer, count, type) {
  const wrongs = new Set();

  if (type === 'natural') {
    const correct = correctAnswer;
    while (wrongs.size < count) {
      let offset;
      const strategy = Math.random();
      if (strategy < 0.3) {
        offset = randInt(1, Math.max(3, Math.floor(Math.abs(correct) * 0.3)));
      } else if (strategy < 0.6) {
        offset = randInt(1, 10);
      } else {
        offset = randInt(1, Math.max(5, Math.floor(Math.abs(correct) * 0.5)));
      }
      const wrong = Math.random() > 0.5 ? correct + offset : correct - offset;
      if (wrong !== correct && wrong >= 0) {
        wrongs.add(wrong);
      }
    }
  } else {
    // Fraction answers
    const { num, den } = correctAnswer;
    while (wrongs.size < count) {
      let wrongNum, wrongDen;
      const strategy = Math.random();
      if (strategy < 0.25) {
        wrongNum = num + randInt(1, 3) * (Math.random() > 0.5 ? 1 : -1);
        wrongDen = den;
      } else if (strategy < 0.5) {
        wrongNum = num;
        wrongDen = den + randInt(1, 3) * (Math.random() > 0.5 ? 1 : -1);
      } else if (strategy < 0.75) {
        wrongNum = num + randInt(1, 2) * (Math.random() > 0.5 ? 1 : -1);
        wrongDen = den + randInt(1, 2) * (Math.random() > 0.5 ? 1 : -1);
      } else {
        wrongNum = num * randInt(2, 3);
        wrongDen = den * randInt(2, 3);
      }
      if (wrongDen > 0 && wrongNum >= 0 && !(wrongNum === num && wrongDen === den)) {
        const key = `${wrongNum}/${wrongDen}`;
        if (!wrongs.has(key)) {
          wrongs.add(key);
        }
      }
    }
  }

  if (type === 'natural') {
    return Array.from(wrongs).map(n => ({ value: n, type: 'natural' }));
  } else {
    return Array.from(wrongs).map(s => {
      const [n, d] = s.split('/').map(Number);
      return { value: { num: n, den: d }, type: 'fraction' };
    });
  }
}

export function generateProblem(settings) {
  const { difficulty, operation, numberType } = settings;

  let problem;
  if (numberType === 'fractions') {
    problem = generateFractionProblem(difficulty, operation);
  } else {
    problem = generateNaturalProblem(difficulty, operation);
  }

  const isFraction = problem.type !== 'natural';
  const wrongAnswers = generateWrongAnswers(
    problem.answer,
    5,
    isFraction ? 'fraction' : 'natural'
  );

  const correctOption = isFraction
    ? { value: problem.answer, type: 'fraction', correct: true, mixed: !!problem.isMixed }
    : { value: problem.answer, type: 'natural', correct: true };

  const allOptions = [correctOption, ...wrongAnswers.map(w => ({ ...w, correct: false, mixed: !!problem.isMixed }))];

  // Shuffle
  for (let i = allOptions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
  }

  return { ...problem, options: allOptions };
}

export { gcd, simplifyFraction };
